/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import ButtonSubmit from "pages/Front/Booking/components/ButtonSubmit";
import {
    DEFAULT_BASENAME,
    DEFAULT_HALL,
    DEFAULT_HALL_ID,
    EMAIL_LOGIN_REQUIRED,
    EMAIL_VERIFICATION_REQUIRED,
    HAS_BASENAME
} from "helpers/config";
import {HallCode} from "types/enums/HallCode";
import BookingAction from "store/booking/BookingAction";
import {getNewStep} from "utils/utility";
import {Company} from "types/entities/Company";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {useDispatch} from "useDispatch";
import {useTypedSelector} from "helpers/reducers";
import {toast} from "react-toastify";
import {RouteComponentProps, withRouter} from "react-router-dom";
import Loader from "components/Loader";
import ShopAction from "store/shop/ShopAction";
import {confirmGiftShopArticleItemsCart} from "helpers/actions";
import {ThunkAction} from "model/store/thunk";
import {CompanySchema} from "../../Schemas/companySchema";
import {AddressMandatoryArboparkSchema, AddressMandatorySchema} from "../../Schemas/adressSchema";
import CompanyForm from "../CompanyForm";
import {CompanyInputs} from "types/entities/specific/CompanyInputs";
import VerificationCardForShop from "pages/Front/Booking/components/BookingCustomer/VerificationCardForShop";
import {FieldErrors} from "react-hook-form/dist/types/errors";
import {CountryCode} from "libphonenumber-js";
import {countryList, PhoneCountry} from "utils/countryUtils";
import * as yup from "yup";
import "yup-phone-lite";

export enum CustomerType {
    Private = "Private",
    Business = "Business",
}

interface BookingCheckoutFormProps extends RouteComponentProps {
    company: Company | null;
    children?: React.ReactNode | React.ReactNode[];
}

const defaultPhoneCountryTextCode = "CH";

const BookingCheckoutForm = (props: BookingCheckoutFormProps) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const bookingState = useTypedSelector(state => state.bookingState);
    const itemsCart = useTypedSelector(state => state.shopState.itemsCart);
    const emailVerified = EMAIL_VERIFICATION_REQUIRED ? useTypedSelector(state => state.bookingState.bookingProcess.emailVerified) : true;
    const emailLogin = EMAIL_LOGIN_REQUIRED ? useTypedSelector(state => state.bookingState.bookingProcess.emailLogin) : true;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedCustomerType, setSelectedCustomerType] = useState<CustomerType>(CustomerType.Private);
    const defaultValues = props.company
        ? {
              ...(props.company || {}),
              is_newsletter_enabled: false,
              company_name: (props.company && props.company.name) || "",
              company_type: false,
          }
        : {
              ...{},
              company_type: false,
              tos: false,
          };
    const defaultCountry = countryList.find(item => item.textCode === defaultPhoneCountryTextCode);
    const defaultPhoneCountry: PhoneCountry = defaultCountry ? defaultCountry : countryList[161];
    const [selectedCountry, setSelectedCountry] = useState<PhoneCountry>(defaultPhoneCountry ? defaultPhoneCountry : countryList[0]);
    const schema =
        DEFAULT_HALL === HallCode.Zwolle ? CompanySchema : DEFAULT_HALL === HallCode.ArboPark ? AddressMandatoryArboparkSchema : AddressMandatorySchema;
    const updatedSchema = schema.shape({
        phone: yup
            .string()
            .phone(selectedCountry?.textCode as CountryCode, t("Phone must be a valid phone number for region ") + selectedCountry?.name)
            .test("startsWithPlus", t("The phone number must start with the country code ") + selectedCountry?.code, function (value) {
                return (value || "").startsWith(selectedCountry?.code);
            })
            .required(),
    });
    const {
        control,
        register,
        handleSubmit,
        clearErrors,
        setValue,
        setError,
        formState: {errors},
    } = useForm<CompanyInputs>({
        defaultValues: defaultValues,
        resolver: yupResolver(updatedSchema),
    });

    const setValueHandler = (name, value) => {
        setValue(name, value);
    };

    // TODO: jakub musis si poriesit captureCustomEvent, neviem kde je to potrebne

    const onErrorCompany = (data: FieldErrors) => {
        if (!emailVerified) {
            return toast.error("Email is not verified");
        }

        return toast.error("One or more fields are not filled");
    };

    const onSubmitCompany = (data: any) => {
        setIsSubmitting(true);

        data.hall_id = DEFAULT_HALL_ID;
        if (itemsCart.shopArticleItemsCart) {
            data.shop_article_items_cart_id = itemsCart.shopArticleItemsCart.id;
        } else {
            toast.error("Shop not set");
            setIsSubmitting(false);
            return Promise.reject("Shop not set");
        }

        if (emailVerified) {
            data.email_verified = true;
        } else {
            toast.error("Email is not verified");
            setIsSubmitting(false);
            return Promise.reject("Email is not verified");
        }

        if (data.company_type == false) {
            delete data.cin;
            delete data.company_name;
            delete data.postal_code;
            delete data.city;
            delete data.street_address;
        }

        return dispatch<ThunkAction<any, any>>(ShopAction.fetchCreateNewDriverForItemsCart(data))
            .then(r => {
                payNowHandler();
            })
            .catch(err => {
                setIsSubmitting(false);
                return Promise.reject("Something went wrong, try again later");
            });
    };

    const payNowHandler = () => {
        if (itemsCart) {
            if (itemsCart.shopArticleItems.length > 0 && itemsCart.shopArticleItemsCart !== null) {
                const url = HAS_BASENAME ? "/" + DEFAULT_BASENAME + "/" : "/";
                dispatch(confirmGiftShopArticleItemsCart(url + "booking/giftcard/paid/", itemsCart.shopArticleItemsCart)).then(r => {
                    setIsSubmitting(false);
                });
            } else {
                setIsSubmitting(false);
                toast.warning(t("Your shopping cart is empty"));
            }

            dispatch(BookingAction.setCurrentNavigationStep(getNewStep(bookingState, 6)));
        }
    };

    if (!itemsCart) {
        return null;
    }

    useEffect(() => {
        setValue("company_type", selectedCustomerType === CustomerType.Business);
    }, [selectedCustomerType]);

    return (
        <div className={"BookingCheckoutFormHolder w-full m-auto flex-wrap sm:flex-nowrap md:space-x-6"}>
            <form
                autoComplete="off"
                className={"BookingCheckoutForm flex flex-col md:flex-row space-y-6 sm:gap-6 sm:space-y-0 justify-between flex-grow h-full columns-2"}
                name={"BookingCheckoutForm"}
                onSubmit={handleSubmit(onSubmitCompany, onErrorCompany)}
            >
                <div className={"w-full md:basis-[50%] md:w-[50%] lg:basis-[45%] lg:w-[45%]"}>
                    <input {...register("id")} type="hidden" />
                    <input {...register("email_verified")} type="hidden" value={emailVerified ? 1 : 0} />
                    <input type={"hidden"} {...register("required_on_package")} defaultValue={1} />
                    <div className={"basis-[50%] flex-shrink-1 flex-grow-1 flex flex-col space-y-6"}>
                        {EMAIL_LOGIN_REQUIRED && (
                            <VerificationCardForShop
                                defaultValues={defaultValues}
                                errors={errors}
                                register={register}
                                schema={updatedSchema}
                                setError={setError}
                                setValue={setValueHandler}
                            />
                        )}

                        <CompanyForm
                            clearError={clearErrors}
                            control={control}
                            defaultValues={defaultValues}
                            errors={errors}
                            origin={"gutschein"}
                            register={register}
                            schema={updatedSchema}
                            selectedCountry={selectedCountry}
                            selectedCustomerType={selectedCustomerType}
                            setError={setError}
                            setSelectedCountry={setSelectedCountry}
                            setSelectedCustomerType={setSelectedCustomerType}
                            setValue={setValueHandler}
                        />
                    </div>
                </div>
                <div className={"w-full md:basis-[50%] md:w-[50%] lg:basis-[55%] lg:w-[55%]"}>
                    <div className={"flex flex-col space-y-6"}>
                        {props.children}
                        <div className={"BookingRecapitulationCheckoutForm flex flex-col mt-8 space-y-8 w-full"}>
                            {!isSubmitting ? (
                                <div className={"space-y-8"}>
                                    <CardComponent>
                                        <div className={"flex flex-col space-y-1"}>
                                            <em className={"leading-4"}>{t("Your payment will be processed on the site of our payment provider")}</em>
                                            <hr />
                                            <div className={"flex flex-col pt-1"}>
                                                <div className={"flex flex-col max-w-xs m-auto w-full"}>
                                                    <ButtonSubmit
                                                        buttonType={"cta"}
                                                        className={"rounded-md"}
                                                        icon={"card"}
                                                        id={"ButtonPayNow"}
                                                        label={t("Pay now")}
                                                        onClick={() => handleSubmit(onSubmitCompany, onErrorCompany)}
                                                    />
                                                </div>
                                                {/*<img alt={"Stripe logo"} className={"h-6 mt-4"} src={"/img/stripe.svg"} />*/}
                                            </div>
                                        </div>
                                    </CardComponent>
                                </div>
                            ) : (
                                <div>
                                    <CardComponent>
                                        <div className={"flex flex-col space-y-1"}>
                                            <ul className={"list-none text-black font-bold flex flex-col items-center"}>
                                                <li>{t("You have a time limit to finish the payment in 15 minutes.")}</li>
                                                <li>{t("When the time will expire, the booking will be cancelled")}</li>
                                            </ul>
                                            <hr />
                                            <div className={"flex flex-col w-full justify-center items-center pt-1"}>
                                                <span>{t("Processing")}</span>
                                                <Loader naked />
                                            </div>
                                        </div>
                                    </CardComponent>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default withRouter(BookingCheckoutForm);
